import { sum, uniq } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { notNullish } from '@paragon/utilities';

import { HeightCache } from '../ComponentGeometryCaches';
import { RootState } from '../store';
import { getDistanceFormatterSelector } from './getDistanceFormatterSelector';
import { getProductionComponentInstancesByBundleGuid } from './getProductionComponentInstancesByBundleGuid';
import { getProductionComponentInstancesByScheduleGroupGuid } from './productionComponentInstances';
import { getProductionGroupsByProjectGuid } from './productionGroups';
import {
  getBundleGuidFromRowId,
  getScheduleGroupGridRowsByScheduleGroupGuid,
  GridRow,
  isBundleRow,
} from './scheduleGroupGrid';
import { getStatusesByScheduleGroupGuid } from './scheduleGroups';
import { sumComponentValueForInstances } from './sumComponentValueInBundle';
import { getLoadedTrussesByAnalysisSetGuid, getTrussMeasurements } from './trusses';

/**
 * Rows shown in the history grid are ones for ScheduleGroups where every instance in the ScheduleGroup has a status of
 * "Delivered".
 */
export const getHistoryGridRows = createSelector(
  getScheduleGroupGridRowsByScheduleGroupGuid,
  getStatusesByScheduleGroupGuid,
  (state: RootState) => state.historyGrid.projectGuidFilter,
  getProductionComponentInstancesByScheduleGroupGuid,
  getProductionGroupsByProjectGuid,

  (
    scheduleGroupGridRowsByScheduleGroupGuid,
    statusesByScheduleGroupGuid,
    projectGuidFilter,
    productionComponentInstancesByScheduleGroupGuid,
    productionGroupsByProjectGuid,
  ): readonly GridRow[] =>
    projectGuidFilter === null
      ? []
      : Object.entries(scheduleGroupGridRowsByScheduleGroupGuid)
          .filter(([scheduleGroupGuid]) => {
            const isFullyDelivered =
              statusesByScheduleGroupGuid[scheduleGroupGuid].length === 1 &&
              statusesByScheduleGroupGuid[scheduleGroupGuid][0] === 'Delivered';
            if (!isFullyDelivered) return false;

            const instancesForScheduleGroup = productionComponentInstancesByScheduleGroupGuid[scheduleGroupGuid];
            const productionGroupGuidsForScheduleGroup = uniq(
              instancesForScheduleGroup.map((instance) => instance.productionGroupGuid),
            );
            return productionGroupsByProjectGuid[projectGuidFilter].some((productionGroup) =>
              productionGroupGuidsForScheduleGroup.includes(productionGroup.guid),
            );
          })
          .flatMap(([_, rowsForScheduleGroup]) => rowsForScheduleGroup),
);

export const getHistoryGridCheckedBundleGuids = createSelector(
  (state: RootState) => state.historyGrid.checkedRows,

  (checkedRows) => checkedRows.filter(isBundleRow).map(getBundleGuidFromRowId),
);

export const getHistoryCheckedRowTotals = createSelector(
  getHistoryGridCheckedBundleGuids,
  getProductionComponentInstancesByBundleGuid,
  getTrussMeasurements,
  getLoadedTrussesByAnalysisSetGuid,
  getDistanceFormatterSelector,

  (
    checkedBundleGuids,
    productionComponentInstancesByBundleGuid,
    trussMeasurements,
    loadedTrussesByAnalysisSetGuid,
    distanceFormatter,
  ) => {
    const checkedTrussCountTotal = sum(
      checkedBundleGuids.map((bundleGuid) => productionComponentInstancesByBundleGuid[bundleGuid].length),
    );
    const checkedBoardFeetTotal = `${Math.round(
      sum(
        checkedBundleGuids.map((bundleGuid) =>
          sumComponentValueForInstances(
            productionComponentInstancesByBundleGuid[bundleGuid],
            trussMeasurements,
            (summary) => summary?.boardFeet ?? 0,
          ),
        ),
      ),
    )} BF`;
    const checkedLinearFeetTotal = `${Math.round(
      sum(
        checkedBundleGuids.map((bundleGuid) =>
          sumComponentValueForInstances(
            productionComponentInstancesByBundleGuid[bundleGuid],
            trussMeasurements,
            (summary) => summary?.linearFeet ?? 0,
          ),
        ),
      ),
    )} LF`;
    const uniqueThicknesses = uniq(
      checkedBundleGuids.flatMap((bundleGuid) =>
        productionComponentInstancesByBundleGuid[bundleGuid]
          .map((instance) => loadedTrussesByAnalysisSetGuid[instance.analysisSetGuid]?.thickness)
          .filter(notNullish),
      ),
    );
    const thicknesses =
      uniqueThicknesses.length === 0
        ? null
        : uniqueThicknesses.length > 1
          ? 'Mixed'
          : distanceFormatter(uniqueThicknesses[0]);
    const uniqueHeights = uniq(
      checkedBundleGuids.flatMap((bundleGuid) =>
        productionComponentInstancesByBundleGuid[bundleGuid]
          .map((instance) =>
            instance.analysisSetGuid in loadedTrussesByAnalysisSetGuid
              ? HeightCache.get(instance.analysisSetGuid, loadedTrussesByAnalysisSetGuid[instance.analysisSetGuid])
              : null,
          )
          .filter(notNullish),
      ),
    );
    const heights =
      uniqueHeights.length === 0 ? null : uniqueHeights.length > 1 ? 'Mixed' : distanceFormatter(uniqueHeights[0]);

    return { checkedTrussCountTotal, checkedBoardFeetTotal, checkedLinearFeetTotal, thicknesses, heights };
  },
);
